import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useReducer, useRef } from "react";
import type { Nullable } from "../@types/generic";

type PathParams = {
    pathname: string;
    page: Nullable<string>;
};

export const useIsPageChange = (): boolean => {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const page = searchParams?.get("page");

    const [state, dispatch] = useReducer(
        (
            prevState: PathParams & { isPageChange: boolean },
            newState: PathParams
        ) => ({
            isPageChange:
                prevState.pathname !== newState.pathname ||
                prevState.page !== newState.page,
            page,
            pathname,
        }),
        {
            isPageChange: false,
            page,
            pathname,
        }
    );

    useEffect(() => {
        dispatch({ pathname, page });
    }, [pathname, page, state.isPageChange]);

    return state.isPageChange;
};

export const usePageChanged = (callback: () => void): void => {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const page = searchParams?.get("page") ?? null;
    const callbackRef = useRef(callback);
    const paramsRef = useRef({ pathname, page });

    useEffect(() => {
        callbackRef.current = callback;
    });

    useEffect(() => {
        if (
            paramsRef.current.pathname === pathname &&
            paramsRef.current.page === page
        ) {
            return;
        }

        callbackRef.current();
        paramsRef.current = { pathname, page };
    }, [pathname, page]);
};
